import React, { useRef } from "react";

import { styled } from "@mui/material";
import { getFontWeightAndStyle } from "@src/formatters";

import { ProgressBarBreakpoint } from "../../../../ProgressBarView/types";
import { useProgressBarContext } from "../../../context/ProgressBarContext";

interface BreakpointLabelsProps {
  onMarginChange: (type: "left" | "right", margin: number) => void;
}

export const BreakpointLabels = (props: BreakpointLabelsProps) => {
  const { state } = useProgressBarContext();
  return (
    <Container className={"ig-labels"}>
      {state.breakpoints.map((b, index) => {
        const onWidth = getOnWidth(props, index, state.breakpoints.length);
        return <BreakpointLabel key={index} breakpoint={b} onWidth={onWidth} />;
      })}
    </Container>
  );
};

interface BreakpointLabelProps {
  breakpoint: ProgressBarBreakpoint;
  onWidth?: (width: number) => void;
}

const BreakpointLabel = ({ breakpoint, onWidth }: BreakpointLabelProps) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const widthRef = useRef(0);
  const completed = breakpoint.progress >= 100;
  const setElement = (el: HTMLDivElement | null) => {
    elementRef.current = el;
    if (el && onWidth) {
      const width = el.clientWidth;
      if (width !== widthRef.current) {
        widthRef.current = width;
        onWidth(width);
      }
    }
  };

  return (
    <Label className={"ig-label"} completed={completed}>
      <span ref={setElement}>{breakpoint.label ?? ""}</span>
    </Label>
  );
};

function getOnWidth(
  props: BreakpointLabelsProps,
  index: number,
  count: number
) {
  if (index === count - 1) {
    if (props.onMarginChange) {
      return (width: number) => props.onMarginChange("right", 0.5 * width);
    }
  } else if (index === 0) {
    if (props.onMarginChange) {
      return (width: number) => props.onMarginChange("left", 0.5 * width);
    }
  }
}

const Container = styled("div")(() => {
  const { state } = useProgressBarContext();
  return {
    display: "flex",
    width: "100%",
    justifyContent:
      state.breakpoints.length === 1 ? "flex-end" : "space-between",
  };
});

const Label = styled("div")<{ completed: boolean }>(({ completed }) => {
  const { config } = useProgressBarContext();
  const { fontWeight, fontStyle } = getFontWeightAndStyle(
    config.desktop.widget.styles.typography.breakpointText.fontStyle
  );
  const fontSize =
    config.desktop.widget.styles.typography.breakpointText.fontSize;
  return {
    width: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    userSelect: "none",
    color: completed
      ? config.desktop.widget.styles.textColors.breakpointTextActive
      : config.desktop.widget.styles.textColors.breakpointTextInactive,
    fontSize,
    fontStyle,
    fontWeight,
  };
});
